import {
  DRONE_MAX_FLEET,
  DRONE_MAX_RANGE_METERS,
  DRONE_MIN_DEPLOY_BATTERY_PCT,
  DRONE_RECOVER_RANGE_METERS,
  DRONE_REPAIR_RANGE_METERS,
  HULL_ZONE_LABELS
} from '../../shared/constants.js';
import { nearestHullDamage } from '../../shared/hullDamage.js';
import type { ControlInput, DroneState, GameSnapshot, RoomState } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { DroneCameraView } from '../rendering/DroneCameraView.js';
import { DEFAULT_GRAPHICS_QUALITY, type GraphicsQuality } from '../rendering/quality.js';
import { DEFAULT_RENDER_TUNING, type RenderTuning } from '../rendering/renderTuning.js';
import { htmlEl } from './stationDisplays.js';

type DroneRoomControllerDeps = {
  controls: HTMLDivElement | null;
  /** Side zones of the controller page: the pilot controls live here so the centre can be all camera. */
  leftZone?: HTMLElement | null;
  rightZone?: HTMLElement | null;
  emitControl: (input: ControlInput) => void;
  getRenderTuning?: () => RenderTuning;
  getGraphicsQuality?: () => GraphicsQuality;
  getFpsOverride?: () => number | undefined;
};

export interface DroneRoomController {
  render: () => void;
  setSnapshot: (snapshot: GameSnapshot) => void;
  setRoomState: (state: RoomState) => void;
  setRenderTuning: (tuning: RenderTuning) => void;
  dispose: () => void;
}

type Axes = { forward: number; turn: number; vertical: number; strafe: number };

// Keyed by quality name (not Record<GraphicsQuality, ...>) so a quality tier added later still falls back sensibly.
const GRAIN_FPS_BY_QUALITY: Record<string, number> = { low: 0, medium: 12, high: 24, ultra: 24 };
const GRAIN_SIZE_BY_QUALITY: Record<string, [number, number]> = { low: [0, 0], medium: [128, 72], high: [192, 108], ultra: [192, 108] };

/** Vertical finger drag tilts the camera this many degrees per pixel. */
const DRONE_LOOK_DEGREES_PER_PIXEL = 0.3;
const DRONE_LOOK_PITCH_LIMIT_DEGREES = 80;
/** Turning is a joystick: how far the finger is from where it touched down sets the turn rate. */
const DRONE_TURN_DEADZONE_PX = 8;
const DRONE_TURN_FULL_DEFLECTION_PX = 100;
const DRONE_TURN_SEND_INTERVAL_MS = 50;

const formatSeconds =(ms: number): string => `${Math.max(0, Math.ceil(ms / 1000))}s`;

const batteryTone = (battery: number): string => (battery <= 15 ? 'crit' : battery <= 35 ? 'warn' : 'ok');

export const createDroneRoomController = ({
  controls,
  leftZone = null,
  rightZone = null,
  emitControl,
  getRenderTuning = () => DEFAULT_RENDER_TUNING,
  getGraphicsQuality = () => DEFAULT_GRAPHICS_QUALITY,
  getFpsOverride
}: DroneRoomControllerDeps): DroneRoomController => {
  let view: DroneCameraView | null = null;
  let viewHost: HTMLDivElement | null = null;
  let connectedDroneId: string | null = null;
  let grainTimerId: number | null = null;
  let keyDownHandler: ((event: KeyboardEvent) => void) | null = null;
  let keyUpHandler: ((event: KeyboardEvent) => void) | null = null;
  let blurHandler: (() => void) | null = null;

  // Finger drag on the feed: sideways offset = turn rate (joystick), up/down tilts the camera.
  let lookPitch = 0;
  let lastAxesSentMs = 0;

  const axes: Axes = { forward: 0, turn: 0, vertical: 0, strafe: 0 };
  let lastSentAxes: Axes = { forward: 0, turn: 0, vertical: 0, strafe: 0 };

  // DOM references (rebuilt by render()).
  let hud: {
    root: HTMLDivElement;
    name: HTMLElement;
    battery: HTMLElement;
    batteryFill: HTMLElement;
    distance: HTMLElement;
    depth: HTMLElement;
    heading: HTMLElement;
    cargo: HTMLElement;
    status: HTMLElement;
    damage: HTMLElement;
    noSignal: HTMLElement;
    noSignalText: HTMLElement;
    controlsRoot: HTMLElement;
    sides: HTMLElement[];
    bayTitle: HTMLElement;
    bayAlert: HTMLElement;
    lightsButton: HTMLButtonElement;
    actionButton: HTMLButtonElement;
    recoverButton: HTMLButtonElement;
    disconnectButton: HTMLButtonElement;
    bay: HTMLElement;
  } | null = null;
  let baySignature = '';

  const myId = (): string => controllerState.currentPlayerId ?? '';

  const myDrone = (snapshot: GameSnapshot | null | undefined): DroneState | null =>
    snapshot?.drones?.find((drone) => drone.mode === 'deployed' && drone.controllerId === myId()) ?? null;

  const sendAxes = (): void => {
    if (!connectedDroneId) return;
    if (axes.forward === lastSentAxes.forward && axes.turn === lastSentAxes.turn && axes.vertical === lastSentAxes.vertical && axes.strafe === lastSentAxes.strafe) return;
    // Analog turning changes every pointer move; keep the stream modest (releasing back to 0 always goes out).
    const now = performance.now();
    if (axes.turn !== 0 && axes.turn !== lastSentAxes.turn && axes.forward === lastSentAxes.forward
      && axes.vertical === lastSentAxes.vertical && axes.strafe === lastSentAxes.strafe && now - lastAxesSentMs < DRONE_TURN_SEND_INTERVAL_MS) return;
    lastAxesSentMs = now;
    lastSentAxes = { ...axes };
    emitControl({ action: 'drone_move', droneId: connectedDroneId, forward: axes.forward, turn: axes.turn, vertical: axes.vertical, strafe: axes.strafe });
  };

  const releaseAxes = (): void => {
    axes.forward = 0;
    axes.turn = 0;
    axes.vertical = 0;
    axes.strafe = 0;
    sendAxes();
  };

  const makeHoldButton = (label: string, axis: keyof Axes, direction: 1 | -1, className = ''): HTMLButtonElement => {
    const button = htmlEl('button', `drone-pad-btn ${className}`, label);
    button.type = 'button';
    const press = (event: Event): void => {
      event.preventDefault();
      axes[axis] = direction;
      button.classList.add('held');
      sendAxes();
    };
    const release = (): void => {
      if (axes[axis] === direction) axes[axis] = 0;
      button.classList.remove('held');
      sendAxes();
    };
    button.addEventListener('pointerdown', (event) => {
      button.setPointerCapture?.(event.pointerId);
      press(event);
    });
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    button.addEventListener('lostpointercapture', release);
    button.addEventListener('contextmenu', (event) => event.preventDefault());
    return button;
  };

  /**
   * Put a finger on the feed and push it left or right: the further from where it touched down, the faster the
   * drone turns, and it keeps turning for as long as the finger is held there (even at the screen edge).
   * Up/down tilts the camera.
   */
  const bindLookDrag = (feed: HTMLElement, meter: HTMLElement, meterDot: HTMLElement): void => {
    let pointerId: number | null = null;
    let originX = 0;
    let lastY = 0;
    const setTurn = (offsetPx: number): void => {
      const magnitude = Math.max(0, Math.abs(offsetPx) - DRONE_TURN_DEADZONE_PX) / (DRONE_TURN_FULL_DEFLECTION_PX - DRONE_TURN_DEADZONE_PX);
      axes.turn = Math.sign(offsetPx) * Math.min(1, magnitude);
      meterDot.style.left = `${50 + axes.turn * 50}%`;
      sendAxes();
    };
    feed.addEventListener('pointerdown', (event) => {
      if (!connectedDroneId || pointerId !== null) return;
      if ((event.target as HTMLElement | null)?.closest('button')) return;
      pointerId = event.pointerId;
      originX = event.clientX;
      lastY = event.clientY;
      meter.classList.add('active');
      meterDot.style.left = '50%';
      feed.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    });
    feed.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId || !connectedDroneId) return;
      const dy = event.clientY - lastY;
      lastY = event.clientY;
      if (dy !== 0) {
        lookPitch = Math.max(-DRONE_LOOK_PITCH_LIMIT_DEGREES, Math.min(DRONE_LOOK_PITCH_LIMIT_DEGREES, lookPitch - dy * DRONE_LOOK_DEGREES_PER_PIXEL));
        view?.setLook(null, lookPitch);
      }
      setTurn(event.clientX - originX);
    });
    const end = (event: PointerEvent): void => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      meter.classList.remove('active');
      axes.turn = 0;
      sendAxes();
    };
    feed.addEventListener('pointerup', end);
    feed.addEventListener('pointercancel', end);
    feed.addEventListener('lostpointercapture', end);
    feed.addEventListener('contextmenu', (event) => event.preventDefault());
  };

  const startGrain =(canvas: HTMLCanvasElement): void => {
    const quality = getGraphicsQuality();
    const fps = GRAIN_FPS_BY_QUALITY[quality] ?? 24;
    const [width, height] = GRAIN_SIZE_BY_QUALITY[quality] ?? [192, 108];
    if (fps <= 0 || width === 0) {
      canvas.classList.add('static-grain');
      return;
    }
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const image = ctx.createImageData(width, height);
    const draw = (): void => {
      const data = image.data;
      for (let i = 0; i < data.length; i += 4) {
        const value = (Math.random() * 255) | 0;
        data[i] = value;
        data[i + 1] = value;
        data[i + 2] = value;
        data[i + 3] = 255;
      }
      ctx.putImageData(image, 0, 0);
    };
    draw();
    grainTimerId = window.setInterval(draw, Math.round(1000 / fps));
  };

  const stopGrain = (): void => {
    if (grainTimerId !== null) {
      window.clearInterval(grainTimerId);
      grainTimerId = null;
    }
  };

  const bindKeys = (): void => {
    const keyAxis = (event: KeyboardEvent): { axis: keyof Axes; direction: 1 | -1 } | null => {
      switch (event.key) {
        case 'w': case 'W': case 'ArrowUp': return { axis: 'forward', direction: 1 };
        case 's': case 'S': case 'ArrowDown': return { axis: 'forward', direction: -1 };
        case 'a': case 'A': case 'ArrowLeft': return { axis: 'turn', direction: -1 };
        case 'd': case 'D': case 'ArrowRight': return { axis: 'turn', direction: 1 };
        case 'q': case 'Q': return { axis: 'strafe', direction: -1 };
        case 'e': case 'E': return { axis: 'strafe', direction: 1 };
        case 'r': case 'R': return { axis: 'vertical', direction: 1 };
        case 'f': case 'F': return { axis: 'vertical', direction: -1 };
        default: return null;
      }
    };
    keyDownHandler = (event) => {
      if (!connectedDroneId || event.repeat) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      const mapped = keyAxis(event);
      if (!mapped) return;
      event.preventDefault();
      axes[mapped.axis] = mapped.direction;
      sendAxes();
    };
    keyUpHandler = (event) => {
      const mapped = keyAxis(event);
      if (!mapped) return;
      if (axes[mapped.axis] === mapped.direction) axes[mapped.axis] = 0;
      sendAxes();
    };
    blurHandler = releaseAxes;
    window.addEventListener('keydown', keyDownHandler);
    window.addEventListener('keyup', keyUpHandler);
    window.addEventListener('blur', blurHandler);
  };

  const unbindKeys = (): void => {
    if (keyDownHandler) window.removeEventListener('keydown', keyDownHandler);
    if (keyUpHandler) window.removeEventListener('keyup', keyUpHandler);
    if (blurHandler) window.removeEventListener('blur', blurHandler);
    keyDownHandler = keyUpHandler = blurHandler = null;
  };

  const playerName = (playerId: string | null): string => {
    if (!playerId) return 'nobody';
    return controllerState.lastRoomState?.players.find((player) => player.id === playerId)?.name ?? 'another pilot';
  };

  const buildBay = (snapshot: GameSnapshot | null): void => {
    if (!hud) return;
    const drones = snapshot?.drones ?? [];
    const docked = snapshot?.submarine.docked ?? false;
    const signature = JSON.stringify(drones.map((d) => [d.id, d.mode, d.controllerId, d.battery >= DRONE_MIN_DEPLOY_BATTERY_PCT, docked]));
    if (signature === baySignature) return;
    baySignature = signature;
    hud.bay.innerHTML = '';
    if (drones.length === 0) {
      hud.bay.append(htmlEl('div', 'drone-bay-empty', 'No drones aboard. Buy one from the shop at a base.'));
      return;
    }
    for (const drone of drones) {
      const row = htmlEl('div', 'drone-bay-row');
      row.dataset.droneId = drone.id;
      const info = htmlEl('div', 'drone-bay-info');
      info.append(htmlEl('div', 'drone-bay-name', drone.name));
      const state = htmlEl('div', 'drone-bay-state');
      state.dataset.role = 'state';
      info.append(state);
      const stats = htmlEl('div', 'drone-bay-stats');
      stats.dataset.role = 'stats';
      info.append(stats);
      const bar = htmlEl('div', 'drone-bay-bar');
      const fill = htmlEl('div', 'drone-bay-bar-fill');
      fill.dataset.role = 'fill';
      bar.append(fill);
      info.append(bar);
      row.append(info);

      const button = htmlEl('button', 'drone-bay-btn');
      button.type = 'button';
      button.dataset.role = 'button';
      button.addEventListener('click', () => {
        const latest = controllerState.lastSnapshot?.drones?.find((d) => d.id === drone.id);
        if (!latest) return;
        if (latest.mode === 'docked') emitControl({ action: 'drone_deploy', droneId: latest.id });
        else if (latest.mode === 'disconnected') emitControl({ action: 'drone_connect', droneId: latest.id });
      });
      row.append(button);
      hud.bay.append(row);
    }
  };

  const refreshBay = (snapshot: GameSnapshot): void => {
    if (!hud) return;
    const docked = snapshot.submarine.docked;
    for (const row of hud.bay.querySelectorAll<HTMLElement>('.drone-bay-row')) {
      const drone = snapshot.drones?.find((d) => d.id === row.dataset.droneId);
      if (!drone) continue;
      const state = row.querySelector<HTMLElement>('[data-role=state]')!;
      const fill = row.querySelector<HTMLElement>('[data-role=fill]')!;
      const stats = row.querySelector<HTMLElement>('[data-role=stats]')!;
      const cargoUnits = Object.values(drone.cargo).reduce((sum, quantity) => sum + quantity, 0);
      stats.textContent = [
        `Battery ${Math.round(drone.battery)}%`,
        `Cargo ${cargoUnits}/${drone.cargoCapacity}`,
        drone.mode === 'docked' ? 'Pilot: none' : `Pilot: ${drone.controllerId ? playerName(drone.controllerId) : 'none'}`,
        drone.mode === 'docked' ? '' : `Range ${drone.distanceToSub.toFixed(1)} m`
      ].filter(Boolean).join('  |  ');
      const button = row.querySelector<HTMLButtonElement>('[data-role=button]')!;
      fill.style.width = `${Math.round(drone.battery)}%`;
      fill.dataset.tone = batteryTone(drone.battery);
      const mine = drone.controllerId === myId();
      row.classList.toggle('mine', mine && drone.mode === 'deployed');
      row.classList.toggle('lost-soon', drone.mode === 'disconnected');
      button.hidden = false;
      button.disabled = false;
      if (drone.mode === 'docked') {
        state.textContent = drone.battery < 100 ? `Docked - charging ${Math.round(drone.battery)}%` : 'Docked - fully charged';
        button.textContent = 'DEPLOY';
        if (docked) {
          button.disabled = true;
          state.textContent += ' (undock to launch)';
        } else if (drone.battery < DRONE_MIN_DEPLOY_BATTERY_PCT) {
          button.disabled = true;
        }
      } else if (drone.mode === 'deployed') {
        state.textContent = mine ? 'CONNECTED - you are piloting' : `Piloted by ${playerName(drone.controllerId)}`;
        button.hidden = true;
      } else {
        const reason = drone.disconnectReason === 'out_of_range' ? 'out of range' : 'no pilot';
        state.textContent = `SIGNAL LOST (${reason}) - lost in ${formatSeconds(drone.lostInMs ?? 0)}`;
        button.textContent = 'RECONNECT';
        button.disabled = drone.distanceToSub > (drone.maxRange ?? DRONE_MAX_RANGE_METERS);
      }
    }
  };

  const updateHud = (snapshot: GameSnapshot): void => {
    if (!hud) return;
    const drone = myDrone(snapshot);
    const nextId = drone?.id ?? null;
    if (nextId !== connectedDroneId) {
      releaseAxes();
      connectedDroneId = nextId;
      lastSentAxes = { forward: 0, turn: 0, vertical: 0, strafe: 0 };
      lookPitch = 0;
      view?.setLook(null, 0);
      view?.setDroneId(nextId);
      view?.setActive(nextId !== null);
    }
    hud.root.classList.toggle('connected', drone !== null);
    hud.controlsRoot.classList.toggle('disabled', drone === null);
    for (const side of hud.sides) side.hidden = drone === null;
    hud.bayTitle.textContent = `DRONE BAY - ${snapshot.drones?.length ?? 0} of ${DRONE_MAX_FLEET} drones`;
    const waitingForMe = snapshot.drones?.find((d) => d.mode === 'disconnected' && d.lastControllerId === myId());
    hud.bayAlert.textContent = waitingForMe
      ? `Signal lost with ${waitingForMe.name} - reconnect within ${formatSeconds(waitingForMe.lostInMs ?? 0)} or lose it forever!`
      : snapshot.submarine.docked && (snapshot.drones?.length ?? 0) > 0 ? 'The submarine is docked - undock to launch a drone.' : '';
    hud.bayAlert.classList.toggle('alarm', Boolean(waitingForMe));
    if (!drone) {
      const waiting = snapshot.drones?.find((d) => d.mode === 'disconnected' && d.lastControllerId === myId());
      hud.noSignalText.textContent = waiting
        ? `SIGNAL LOST - ${waiting.name} - reconnect within ${formatSeconds(waiting.lostInMs ?? 0)}`
        : 'NO SIGNAL - select a drone below';
      hud.noSignal.classList.toggle('alarm', Boolean(waiting));
    } else {
      hud.name.textContent = drone.name.toUpperCase();
      hud.battery.textContent = `BATT ${Math.round(drone.battery)}%`;
      hud.batteryFill.style.height = `${Math.round(drone.battery)}%`;
      hud.batteryFill.dataset.tone = batteryTone(drone.battery);
      hud.battery.dataset.tone = batteryTone(drone.battery);
      const rangeLimit = drone.maxRange ?? DRONE_MAX_RANGE_METERS;
      hud.distance.textContent = `RNG ${drone.distanceToSub.toFixed(1)} / ${rangeLimit} m`;
      hud.distance.dataset.tone = drone.distanceToSub > rangeLimit * 0.8 ? 'crit' : drone.distanceToSub > rangeLimit * 0.6 ? 'warn' : 'ok';
      hud.depth.textContent = `DEPTH ${drone.depth.toFixed(1)} m`;
      const heading = ((drone.yaw % 360) + 360) % 360;
      hud.heading.textContent = `HDG ${String(Math.round(heading)).padStart(3, '0')}`;
      const cargoUnits = Object.values(drone.cargo).reduce((sum, quantity) => sum + quantity, 0);
      hud.cargo.textContent = `CARGO ${cargoUnits}/${drone.cargoCapacity}`;
      hud.status.textContent = drone.action === 'repair' ? 'REPAIRING' : drone.action === 'harvest' ? 'HARVESTING' : drone.action === 'interact' ? 'OPERATING' : drone.lightsOn ? 'LIGHTS ON' : 'LIGHTS OFF';

      // Hull damage readout: where the nearest damage is, and whether the drone is close enough to repair it.
      const nearest = nearestHullDamage(snapshot.submarine, drone);
      if (!nearest) {
        hud.damage.textContent = '';
        hud.damage.dataset.state = 'none';
      } else {
        const where = `${HULL_ZONE_LABELS[nearest.spot.zone].toUpperCase()} ${nearest.spot.side.toUpperCase()}`;
        if (drone.action === 'repair') {
          hud.damage.textContent = `AUTOPILOT WELDING - ${where}`;
          hud.damage.dataset.state = 'repairing';
        } else if (nearest.distance <= DRONE_REPAIR_RANGE_METERS) {
          hud.damage.textContent = `DAMAGE IN REACH - ${where} - PRESS REPAIR`;
          hud.damage.dataset.state = 'reach';
        } else {
          hud.damage.textContent = `HULL DAMAGE - ${where} - ${nearest.distance.toFixed(1)} m (REPAIR WITHIN ${DRONE_REPAIR_RANGE_METERS} m)`;
          hud.damage.dataset.state = 'far';
        }
      }

      hud.lightsButton.classList.toggle('on', drone.lightsOn);
      hud.lightsButton.textContent = drone.lightsOn ? 'LIGHTS ON' : 'LIGHTS OFF';
      if (drone.action) {
        hud.actionButton.textContent = drone.action === 'repair' ? 'STOP REPAIR' : 'STOP HARVEST';
        hud.actionButton.disabled = false;
        hud.actionButton.dataset.mode = drone.action;
      } else if (drone.contextAction) {
        hud.actionButton.textContent = drone.contextAction === 'repair' ? 'REPAIR' : drone.contextAction === 'interact' ? 'OPERATE' : 'HARVEST';
        hud.actionButton.disabled = false;
        hud.actionButton.dataset.mode = drone.contextAction;
      } else {
        hud.actionButton.textContent = 'NO TARGET';
        hud.actionButton.disabled = true;
        hud.actionButton.dataset.mode = 'none';
      }
      // Recovery needs the drone beside the bay under the submarine (the server checks the exact range).
      hud.recoverButton.disabled = drone.distanceToSub > DRONE_RECOVER_RANGE_METERS + 1.2;
    }
    buildBay(snapshot);
    refreshBay(snapshot);
  };

  const render = (): void => {
    if (!controls) return;
    dispose();
    controls.classList.add('drone-room-layout');
    controls.classList.remove('camera-role-layout');

    const root = htmlEl('div', 'drone-room');

    // ── Camera feed with the fighter-HUD style overlay ──
    const feed = htmlEl('div', 'drone-feed');
    // The viewport shell forces `position: relative` on its container, so the container gets an explicit size
    // inside an absolutely positioned wrapper (which also carries the colour grade).
    const viewWrap = htmlEl('div', 'drone-feed-view');
    viewHost = htmlEl('div', 'drone-feed-view-host');
    viewWrap.append(viewHost);
    const grain = htmlEl('canvas', 'drone-feed-grain');
    const scan = htmlEl('div', 'drone-feed-scan');
    const vignette = htmlEl('div', 'drone-feed-vignette');

    const hudLayer = htmlEl('div', 'drone-hud');
    hudLayer.innerHTML = `
      <svg class="drone-hud-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path d="M44 50h4M52 50h4M50 44v4M50 52v4" />
        <path d="M8 8h6M8 8v6M92 8h-6M92 8v6M8 92h6M8 92v-6M92 92h-6M92 92v-6" />
        <path d="M30 40h8M62 40h8M34 46h4M62 46h4M34 54h4M62 54h4M30 60h8M62 60h8" />
      </svg>`;
    const name = htmlEl('div', 'drone-hud-name');
    const heading = htmlEl('div', 'drone-hud-heading');
    const distance = htmlEl('div', 'drone-hud-distance');
    const depth = htmlEl('div', 'drone-hud-depth');
    const battery = htmlEl('div', 'drone-hud-battery');
    const cargo = htmlEl('div', 'drone-hud-cargo');
    const status = htmlEl('div', 'drone-hud-status');
    const damage = htmlEl('div', 'drone-hud-damage');
    const batteryTrack = htmlEl('div', 'drone-hud-battery-track');
    const batteryFill = htmlEl('div', 'drone-hud-battery-fill');
    batteryTrack.append(batteryFill);
    hudLayer.append(name, heading, distance, depth, battery, batteryTrack, cargo, status, damage);

    const noSignal = htmlEl('div', 'drone-nosignal');
    const noSignalText = htmlEl('div', 'drone-nosignal-text', 'NO SIGNAL');
    noSignal.append(noSignalText);

    // A second way out that does not depend on the side zones being laid out (portrait phones stack them above/below).
    const exitButton = htmlEl('button', 'drone-exit-btn', 'BAY');
    exitButton.type = 'button';
    exitButton.addEventListener('click', () => {
      if (connectedDroneId) emitControl({ action: 'drone_disconnect', droneId: connectedDroneId });
    });
    feed.append(viewWrap, grain, scan, vignette, hudLayer, noSignal, exitButton);
    const turnMeter = htmlEl('div', 'drone-turn-meter');
    const turnMeterDot = htmlEl('div', 'drone-turn-meter-dot');
    turnMeter.append(turnMeterDot);
    feed.append(turnMeter);
    bindLookDrag(feed, turnMeter, turnMeterDot);

    // ── Controls ──
    const controlsRoot = htmlEl('div', 'drone-screen drone-screen-feed');
    // One full-width column: UP above FWD, the sidestep pair across the full width, BACK above DOWN.
    const leftPad = htmlEl('div', 'drone-pad drone-pad-grid');
    const forward = makeHoldButton('\u25B2 FWD', 'forward', 1, 'move');
    const back = makeHoldButton('\u25BC BACK', 'forward', -1, 'move');
    const up = makeHoldButton('\u25B2 UP', 'vertical', 1, 'height');
    const down = makeHoldButton('\u25BC DOWN', 'vertical', -1, 'height');
    // Sidestep pair sits between FWD and BACK.
    const strafeRow = htmlEl('div', 'drone-strafe-row');
    strafeRow.append(
      makeHoldButton('\u25C0 LEFT', 'strafe', -1, 'move strafe'),
      makeHoldButton('RIGHT \u25B6', 'strafe', 1, 'move strafe')
    );
    leftPad.append(up, forward, strafeRow, back, down);
    const rightPad = htmlEl('div');

    const lightsButton = htmlEl('button', 'drone-action-btn lights', 'LIGHTS OFF');
    lightsButton.type = 'button';
    lightsButton.addEventListener('click', () => {
      if (!connectedDroneId) return;
      const drone = controllerState.lastSnapshot?.drones?.find((d) => d.id === connectedDroneId);
      emitControl({ action: 'drone_lights', droneId: connectedDroneId, active: !(drone?.lightsOn ?? false) });
    });
    const actionButton = htmlEl('button', 'drone-action-btn context', 'NO TARGET');
    actionButton.type = 'button';
    actionButton.addEventListener('click', () => {
      if (connectedDroneId) emitControl({ action: 'drone_action', droneId: connectedDroneId });
    });
    const recoverButton = htmlEl('button', 'drone-action-btn recover', 'RECOVER');
    recoverButton.type = 'button';
    recoverButton.addEventListener('click', () => {
      if (connectedDroneId) emitControl({ action: 'drone_recover', droneId: connectedDroneId });
    });
    const disconnectButton = htmlEl('button', 'drone-action-btn disconnect', 'DISCONNECT');
    disconnectButton.type = 'button';
    disconnectButton.addEventListener('click', () => {
      if (connectedDroneId) emitControl({ action: 'drone_disconnect', droneId: connectedDroneId });
    });
    const actions = htmlEl('div', 'drone-actions');
    actions.append(lightsButton, actionButton, recoverButton, disconnectButton);
    // Pilot console: movement and height in the left zone, lights / context / recover / disconnect in the right zone, the feed alone in the centre.
    const leftCol = htmlEl('div', 'drone-side drone-side-left');
    leftCol.append(leftPad);
    const rightCol = htmlEl('div', 'drone-side drone-side-right');
    rightCol.append(actions);
    controlsRoot.append(feed);
    leftCol.hidden = true;
    rightCol.hidden = true;
    leftZone?.classList.add('drone-zone');
    rightZone?.classList.add('drone-zone');
    leftZone?.append(leftCol);
    rightZone?.append(rightCol);

    // Bay screen: the first level. Pick or reconnect a drone; connecting moves you to the pilot screen.
    const bayScreen = htmlEl('div', 'drone-screen drone-screen-bay');
    const bayHead = htmlEl('div', 'drone-bay-head');
    const bayTitle = htmlEl('div', 'drone-bay-title', 'DRONE BAY');
    const bayAlert = htmlEl('div', 'drone-bay-alert');
    bayHead.append(bayTitle, bayAlert);
    const bay = htmlEl('div', 'drone-bay');
    bayScreen.append(bayHead, bay);

    root.append(bayScreen, controlsRoot);
    controls.append(root);

    hud = {
      root, name, battery, batteryFill, distance, depth, heading, cargo, status, damage, noSignal, noSignalText,
      controlsRoot, sides: [leftCol, rightCol], bayTitle, bayAlert, lightsButton, actionButton, recoverButton, disconnectButton, bay
    };
    baySignature = '';
    startGrain(grain);
    bindKeys();

    view = new DroneCameraView(viewHost, getGraphicsQuality(), getRenderTuning(), getFpsOverride?.());
    view.setActive(false);
    if (new URLSearchParams(window.location.search).has('debug')) (window as unknown as { __droneCamera?: DroneCameraView }).__droneCamera = view;
    if (controllerState.lastRoomState) view.setRoomState(controllerState.lastRoomState);
    if (controllerState.lastSnapshot) {
      view.setSnapshot(controllerState.lastSnapshot);
      updateHud(controllerState.lastSnapshot);
    }
  };

  const setSnapshot = (snapshot: GameSnapshot): void => {
    if (!hud) return;
    view?.setSnapshot(snapshot);
    updateHud(snapshot);
  };

  const dispose = (): void => {
    releaseAxes();
    for (const side of hud?.sides ?? []) side.remove();
    leftZone?.classList.remove('drone-zone');
    rightZone?.classList.remove('drone-zone');
    stopGrain();
    unbindKeys();
    view?.dispose();
    view = null;
    viewHost = null;
    hud = null;
    connectedDroneId = null;
    lastSentAxes = { forward: 0, turn: 0, vertical: 0, strafe: 0 };
  };

  return {
    render,
    setSnapshot,
    setRoomState: (state) => view?.setRoomState(state),
    setRenderTuning: (tuning) => view?.setRenderTuning(tuning),
    dispose
  };
};
